/* eslint-disable @typescript-eslint/no-unused-vars */
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { isPlatformBrowser } from '@angular/common';
import { ReferralNoticeService } from '../../services/referral-notice.service';
import { PENDING_REFERRAL_CODE_KEY } from '../constants/referral.const';

@Injectable({ providedIn: 'root' })
export class AuthGuardService {
  private isBrowser: boolean;
  private referralNotice = inject(ReferralNoticeService);

  constructor(
    private router: Router,
    private authService: AuthService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  private readQueryParam(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    key: string,
  ): string | null {
    let value = next.queryParamMap.get(key) ?? null;

    if (!value && next.root?.queryParamMap) {
      value = next.root.queryParamMap.get(key) ?? null;
    }

    if (!value && state?.url) {
      try {
        const parsed = this.router.parseUrl(state.url);
        value = parsed.queryParamMap.get(key) ?? null;
      } catch {
        // ignore parse errors
      }
    }

    return value;
  }

  private readShareType(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): string | null {
    return this.readQueryParam(next, state, 'shareType');
  }

  private readReferralCode(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): string | null {
    return this.readQueryParam(next, state, 'referralCode');
  }

  private readUserId(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): string | null {
    return this.readQueryParam(next, state, 'userId');
  }

  private readTrackingId(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): string | null {
    // 1️⃣ Direct param on current route
    let trackingId = next.paramMap.get('id');

    // 2️⃣ Walk up parent routes (important for nested routes)
    let parent = next.parent;
    while (!trackingId && parent) {
      trackingId = parent.paramMap.get('id');
      parent = parent.parent;
    }

    // 3️⃣ Fallback: parse from URL
    if (!trackingId && state?.url) {
      try {
        const parsed = this.router.parseUrl(state.url);
        const segments = parsed.root.children['primary']?.segments;
        // /tracking/12724/3 → segments[1] = 12724
        trackingId = segments?.[1]?.path ?? null;
      } catch {
        // ignore
      }
    }

    return trackingId;
  }

  private isTrackingRoute(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (next.routeConfig?.path?.startsWith('tracking')) return true;
    if (next.url?.[0]?.path === 'tracking') return true;
    if (state.url?.includes('/tracking/')) return true;
    return false;
  }

  private persistReferralCode(code: string): void {
    try {
      sessionStorage.setItem(PENDING_REFERRAL_CODE_KEY, code);
    } catch {
      // ignore storage errors
    }
  }

  private clearPendingReferralCode(): void {
    try {
      sessionStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
    } catch {
      // ignore storage errors
    }
  }

  private handleReferralCode(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | null {
    const referralCode = this.readReferralCode(next, state);
    if (!referralCode) {
      return null;
    }

    const hasToken = this.authService.isAuthenticated();

    if (hasToken) {
      this.clearPendingReferralCode();
      this.referralNotice.queueError('ReferralAlreadyLoggedIn');
      return this.router.parseUrl('/services');
    }

    this.persistReferralCode(referralCode);
    return this.router.createUrlTree(['/get-started'], {
      queryParams: { referralCode },
    });
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    // SSR guard
    if (!this.isBrowser) {
      return true;
    }

    const referralResult = this.handleReferralCode(next, state);
    if (referralResult !== null) {
      return referralResult;
    }

    // read shareType robustly
    const shareType = this.readShareType(next, state);
    const isTracking = this.isTrackingRoute(next, state);

    if (shareType === '1') {
      if (isTracking) {
        return true;
      }
      return this.router.parseUrl('/auth/login');
    }

    // allow if it's tracking route and shareType === '0'
    if (isTracking && shareType === '0') {
      const hasToken = !!localStorage.getItem('accessToken');

      if (hasToken) {
        return true;
      }
      const userId = this.readUserId(next, state);
      const trackingId = this.readTrackingId(next, state);
      return this.router.createUrlTree(['/get-started'], {
        queryParams: {
          shareType: '0',
          userId,
          trackingId,
          from: 'tracking', // optional
        },
      });
    }

    // fallback: normal auth check
    const token = this.authService.isAuthenticated();

    if (!token) {
      return this.router.parseUrl('/auth/login');
    }

    return true;
  }
}

export const AuthGuard = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(AuthGuardService).canActivate(next, state);
};
